import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { useUIStore } from '@/store/uiStore';
import { useAuthStore } from '@/store/authStore';
import { useCart } from '@/hooks/useCart';
import { Button } from '@/components/ui/button';
import { ShoppingBag, Trash2, Loader2, AlertCircle, MapPin } from 'lucide-react';
import { useState, useEffect } from 'react';
import { getAddresses, type Address } from '@/services/erpnextAddress';
import { updateCartAddress } from '@/services/erpnextCart';
import { Link } from 'react-router-dom';

const CURRENCY_SYMBOLS: Record<string, string> = {
  CAD: 'CA$',
  USD: 'US$',
  AUD: 'A$',
  EUR: '€',
  GBP: '£',
  AED: 'د.إ',
};

export const CartDrawer = () => {
  const isOpen = useUIStore((state) => state.isCartDrawerOpen);
  const closeDrawer = useUIStore((state) => state.closeCartDrawer);
  const user = useAuthStore((state) => state.user);

  const {
    items,
    total,
    itemCount,
    currency,
    isLoading,
    error,
    updateQuantity,
    removeItem,
    clearCart,
    checkout,
  } = useCart();

  const [isCheckingOut, setIsCheckingOut] = useState(false);
  const [addresses, setAddresses] = useState<Address[]>([]);
  const [isLoadingAddresses, setIsLoadingAddresses] = useState(false);
  const [selectedShippingAddress, setSelectedShippingAddress] = useState<string>('');
  const [selectedBillingAddress, setSelectedBillingAddress] = useState<string>('');

  // Fetch addresses when drawer opens and user is logged in
  useEffect(() => {
    if (isOpen && user) {
      loadAddresses();
    }
  }, [isOpen, user]);

  const loadAddresses = async () => {
    try {
      setIsLoadingAddresses(true);
      const userAddresses = await getAddresses();
      setAddresses(userAddresses);

      // Auto-select primary shipping address if available
      const primaryShipping = userAddresses.find(
        (addr) => addr.is_shipping_address && addr.is_primary_address
      );
      if (primaryShipping) {
        setSelectedShippingAddress(primaryShipping.name);
      } else if (userAddresses.length > 0) {
        // Otherwise select first address
        setSelectedShippingAddress(userAddresses[0].name);
      }

      // Auto-select same address for billing
      if (userAddresses.length > 0) {
        setSelectedBillingAddress(userAddresses[0].name);
      }
    } catch (err) {
      console.error('Failed to load addresses:', err);
    } finally {
      setIsLoadingAddresses(false);
    }
  };

  const handleCheckout = async () => {
    // Require authentication
    if (!user) {
      alert('Please log in to complete your purchase');
      closeDrawer();
      return;
    }

    if (items.length === 0) {
      return;
    }

    // Require address selection
    if (!selectedShippingAddress) {
      alert('Please select a shipping address');
      return;
    }

    if (!selectedBillingAddress) {
      alert('Please select a billing address');
      return;
    }

    try {
      setIsCheckingOut(true);

      // Set shipping and billing addresses on the cart
      await updateCartAddress('shipping', selectedShippingAddress);
      await updateCartAddress('billing', selectedBillingAddress);

      // Place order via ERPNext cart API
      const salesOrderId = await checkout();

      // Success
      closeDrawer();
      alert(
        `Order placed successfully!\n\nOrder ID: ${salesOrderId}\n\nThank you for your order! You will receive a confirmation email shortly.`
      );
    } catch (err) {
      console.error('Checkout error:', err);
      alert(
        `Failed to place order. Please try again.\n\nError: ${
          err instanceof Error ? err.message : 'Unknown error'
        }`
      );
    } finally {
      setIsCheckingOut(false);
    }
  };

  const handleUpdateQuantity = async (itemCode: string, newQty: number) => {
    try {
      await updateQuantity(itemCode, Math.max(1, newQty));
    } catch (err) {
      console.error('Update quantity error:', err);
      alert('Failed to update quantity. Please try again.');
    }
  };

  const handleRemoveItem = async (itemCode: string) => {
    try {
      await removeItem(itemCode);
    } catch (err) {
      console.error('Remove item error:', err);
      alert('Failed to remove item. Please try again.');
    }
  };

  const handleClearCart = async () => {
    if (!confirm('Are you sure you want to clear your cart?')) {
      return;
    }

    try {
      await clearCart();
    } catch (err) {
      console.error('Clear cart error:', err);
      alert('Failed to clear cart. Please try again.');
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={closeDrawer}>
      <SheetContent className="bg-dark-bg-secondary border-dark-border-default w-full sm:max-w-lg">
        <SheetHeader>
          <SheetTitle className="text-dark-text-primary flex items-center gap-2">
            <ShoppingBag className="w-5 h-5" />
            Shopping Cart ({itemCount} {itemCount === 1 ? 'item' : 'items'})
          </SheetTitle>
        </SheetHeader>

        <div className="mt-8 flex flex-col h-[calc(100vh-120px)]">
          {/* Loading State */}
          {isLoading && items.length === 0 && (
            <div className="flex-1 flex items-center justify-center">
              <Loader2 className="w-8 h-8 animate-spin text-dark-text-tertiary" />
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-red-800">Error loading cart</p>
                <p className="text-xs text-red-600 mt-1">{error}</p>
              </div>
            </div>
          )}

          {/* Empty Cart */}
          {!isLoading && items.length === 0 && (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <ShoppingBag className="w-16 h-16 mx-auto mb-4 text-dark-text-tertiary" />
                <p className="text-dark-text-secondary">Your cart is empty</p>
              </div>
            </div>
          )}

          {/* Cart Items */}
          {items.length > 0 && (
            <>
              <div className="flex-1 overflow-y-auto space-y-4">
                {items.map((item) => (
                  <div
                    key={item.item_code}
                    className="flex gap-4 p-4 bg-dark-bg-tertiary rounded-lg"
                  >
                    {item.image && (
                      <img
                        src={item.image}
                        alt={item.item_name}
                        className="w-20 h-20 object-cover rounded-md bg-dark-bg-primary"
                      />
                    )}

                    <div className="flex-1">
                      <h4 className="font-medium text-dark-text-primary mb-1">
                        {item.item_name}
                      </h4>

                      <p className="text-xs text-dark-text-tertiary mb-2">
                        {item.item_code}
                      </p>

                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleUpdateQuantity(item.item_code, item.qty - 1)}
                          disabled={isLoading || item.qty <= 1}
                          className="h-8 w-8 p-0 bg-dark-bg-secondary border-dark-border-default"
                        >
                          -
                        </Button>
                        <span className="text-dark-text-primary w-8 text-center">
                          {item.qty}
                        </span>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleUpdateQuantity(item.item_code, item.qty + 1)}
                          disabled={isLoading}
                          className="h-8 w-8 p-0 bg-dark-bg-secondary border-dark-border-default"
                        >
                          +
                        </Button>
                      </div>
                    </div>

                    <div className="flex flex-col items-end justify-between">
                      <p className="font-semibold text-brand-primary">
                        {CURRENCY_SYMBOLS[currency]}
                        {item.amount.toFixed(2)}
                      </p>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveItem(item.item_code)}
                        disabled={isLoading}
                        className="text-error hover:text-error hover:bg-error/10"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Address Selection */}
              {user && (
                <div className="border-t border-dark-border-subtle pt-4 mt-4 space-y-4">
                  <h3 className="text-sm font-semibold text-dark-text-primary flex items-center gap-2">
                    <MapPin className="w-4 h-4" />
                    Delivery Information
                  </h3>

                  {isLoadingAddresses ? (
                    <div className="flex justify-center py-4">
                      <Loader2 className="w-5 h-5 animate-spin text-dark-text-tertiary" />
                    </div>
                  ) : addresses.length === 0 ? (
                    <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                      <p className="text-sm text-amber-800 mb-2">No saved addresses found</p>
                      <Link to="/account" onClick={closeDrawer}>
                        <Button
                          variant="outline"
                          size="sm"
                          className="w-full bg-white hover:bg-amber-50"
                        >
                          <MapPin className="w-4 h-4 mr-2" />
                          Add Address in Profile
                        </Button>
                      </Link>
                    </div>
                  ) : (
                    <>
                      {/* Shipping Address */}
                      <div>
                        <label className="text-xs text-dark-text-secondary mb-2 block">
                          Shipping Address
                        </label>
                        <select
                          value={selectedShippingAddress}
                          onChange={(e) => setSelectedShippingAddress(e.target.value)}
                          className="w-full px-3 py-2 bg-dark-bg-tertiary border border-dark-border-default rounded-md text-dark-text-primary text-sm"
                        >
                          {addresses.map((addr) => (
                            <option key={addr.name} value={addr.name}>
                              {addr.address_title} - {addr.display}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Billing Address */}
                      <div>
                        <label className="text-xs text-dark-text-secondary mb-2 block">
                          Billing Address
                        </label>
                        <select
                          value={selectedBillingAddress}
                          onChange={(e) => setSelectedBillingAddress(e.target.value)}
                          className="w-full px-3 py-2 bg-dark-bg-tertiary border border-dark-border-default rounded-md text-dark-text-primary text-sm"
                        >
                          {addresses.map((addr) => (
                            <option key={addr.name} value={addr.name}>
                              {addr.address_title} - {addr.display}
                            </option>
                          ))}
                        </select>
                      </div>

                      <Link to="/account" onClick={closeDrawer}>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-xs text-dark-text-tertiary hover:text-brand-primary"
                        >
                          Manage addresses
                        </Button>
                      </Link>
                    </>
                  )}
                </div>
              )}

              {/* Cart Footer */}
              <div className="border-t border-dark-border-subtle pt-4 mt-4 space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-lg font-medium text-dark-text-primary">Total:</span>
                  <span className="text-2xl font-bold text-brand-primary">
                    {CURRENCY_SYMBOLS[currency]}
                    {total.toFixed(2)}
                  </span>
                </div>

                <Button
                  className="w-full bg-brand-primary hover:bg-brand-primary/90 text-white"
                  size="lg"
                  onClick={handleCheckout}
                  disabled={isCheckingOut || isLoading || items.length === 0 || (!!user && addresses.length === 0)}
                >
                  {isCheckingOut ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    'Checkout'
                  )}
                </Button>

                <Button
                  variant="ghost"
                  className="w-full text-dark-text-secondary hover:text-error"
                  onClick={handleClearCart}
                  disabled={isLoading || items.length === 0}
                >
                  Clear Cart
                </Button>
              </div>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};
